import React, { useState, useRef } from 'react'
import dynamic from 'next/dynamic'
import * as BlazeSDK from "@wscsports/blaze-web-sdk"
import { IPlaylistResponse } from '@/types/playlist'
import { TranslationType, IPlaylistScript } from '@/types'
import { WSCPlaceholder } from './WSCPlaceholders'
import { WSCNavigation } from '../Navigation/WSCNavigation'
import { resolveWscPreset, CONTENT_TYPE } from './presets'
import { useTranslation } from '@/localization/useTranslation'

import classNames from 'classnames'
import classes from './styles.module.scss'

const WSCCarouselComponent = dynamic(() => import('./WSCComponent'), { ssr: false })
interface WSCCarouselProps {
  type: BlazeSDK.ContentType
  playlistData: IPlaylistResponse & {
    _id: string
    title: TranslationType
    wsc: {
      dataSource: {
        label: string
        mode: BlazeSDK.ContentType
      }
    }
  }
  playlistScript: IPlaylistScript
  isPlaceholder?: boolean
}

export const WSCCarousel: React.FC<WSCCarouselProps> = (props) => {
  const {
    type,
    isPlaceholder = true,
    playlistScript: script,
    playlistData: data,
  } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { wsc, title } = data || {}
  const { options } = script
  const [isEmpty, setIsEmpty] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const playlistTitle = options?.customTitle || title
  const preset = resolveWscPreset(options?.cardType, type === CONTENT_TYPE.MOMENT ? CONTENT_TYPE.MOMENT : CONTENT_TYPE.STORY)
  const hideTitle = options?.hideTitle === true

  const handleIsLoaded = (isLoaded: boolean) => {
    setIsLoaded(isLoaded)
  }

  const handleIsEmpty = (isEmpty: boolean) => {
    setIsEmpty(isEmpty)
  }

  return (
    <div className={classNames({
      [classes.container]: true,
      [classes.container__hide]: isEmpty,
      [classes.container__story]: type === CONTENT_TYPE.STORY,
      [classes.container__moment]: type === CONTENT_TYPE.MOMENT,
    })}>
      {(isPlaceholder || !isLoaded) &&
        <WSCPlaceholder
          preset={preset}
          items={20}
          hideTitle={hideTitle}
        />
      }
      <div className={classNames({
        [classes.content]: true,
        [classes.content__hide]: !isLoaded || isEmpty,
      })}>
        <div className={classes.header}>
          {!hideTitle && (
            <h2 className={classes.title}>
              {t(playlistTitle)}
            </h2>
          )}
          <WSCNavigation
            containerRef={containerRef}
            isLoaded={isLoaded}
            isEmpty={isEmpty}
            isPlaceholder={isPlaceholder}
          />
        </div>
        <div ref={containerRef}>
          {/* Guard: only mount the blaze widget once wsc.dataSource is available.
              Mounting without it causes the SDK to receive undefined config and crash
              with "Cannot destructure property 'layout' of 'e' as it is undefined"
              (Sentry: FZ-WEB-NEXTJS-EP, FZ-WEB-NEXTJS-EQ) */}
          {wsc?.dataSource && (
            <WSCCarouselComponent
              type={type}
              preset={preset}
              wscData={wsc.dataSource}
              playlistId={script._id}
              isEmpty={isEmpty}
              isLoaded={isLoaded}
              handleIsEmpty={handleIsEmpty}
              handleIsLoaded={handleIsLoaded}
            />
          )}
        </div>
      </div>
    </div>
  )
}
